/**
 * RouteErrorBoundary — handles lazy chunk-load failure (round-2 DA).
 *
 * Phase 4 W4.T4. When `React.lazy` fails to fetch a chunk (deploy in
 * progress, network drop, stale CDN cache), the default behaviour is to
 * propagate the error up to the nearest Error Boundary. Without one,
 * the app blank-screens.
 *
 * This boundary is wrapped around every lazy() route. When the chunk
 * fails:
 *   1. We display a friendly «Не удалось загрузить страницу» panel with
 *      a «Повторить» CTA that hard-reloads — this re-fetches the chunk
 *      manifest from the now-current deploy.
 *   2. The error is logged to the console so devs can pick it up in
 *      live-VPS replay.
 *
 * We classify chunk-load specifically (the `ChunkLoadError` shape Vite
 * emits) so non-chunk runtime errors fall through to the global error
 * boundary in App.tsx.
 */
import * as React from 'react';

import { Button } from '@/components/ui/button';

interface State {
  error: Error | null;
}

interface Props {
  children: React.ReactNode;
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  // Vite emits `ChunkLoadError` with a `name` of 'ChunkLoadError' and a
  // message containing «Loading chunk» / «Failed to fetch dynamically
  // imported module». Match all three so we cover dev, prod, and the
  // Safari preload-failed flavour.
  if (error.name === 'ChunkLoadError') return true;
  const msg = error.message ?? '';
  return /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
    msg,
  );
}

export class RouteErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    console.error('[RouteErrorBoundary] route chunk load failed:', error);
  }

  handleRetry = (): void => {
    // Hard reload — re-pulls the chunk manifest from the current deploy
    // and re-runs the lazy import on a clean module graph.
    if (typeof window !== 'undefined') {
      window.location.reload();
    } else {
      this.setState({ error: null });
    }
  };

  override render(): React.ReactNode {
    if (this.state.error && isChunkLoadError(this.state.error)) {
      return (
        <div
          role="alert"
          data-testid="route-error-boundary"
          className="flex flex-col items-start gap-3 rounded-sm border border-line bg-surface-2 p-6"
        >
          <p className="text-sm font-semibold text-ink">Не удалось загрузить страницу</p>
          <p className="text-xs text-muted">
            Возможно, обновился сайт. Перезагрузите страницу, чтобы получить новую версию.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={this.handleRetry}
            data-testid="route-error-retry"
          >
            Повторить
          </Button>
        </div>
      );
    }
    if (this.state.error) {
      // Non-chunk error → re-throw so the global App-level boundary handles it.
      throw this.state.error;
    }
    return this.props.children;
  }
}
